
import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCustomAuth } from '@/hooks/useCustomAuth'
import { useToast } from '@/hooks/use-toast'
import { useSchools } from '@/hooks/useSchools'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'

interface School {
  id: string
  name: string
  domain: string | null
}

const classOptions = [
  { value: 'Freshman', label: 'Freshman' },
  { value: 'Sophomore', label: 'Sophomore' },
  { value: 'Junior', label: 'Junior' },
  { value: 'Senior', label: 'Senior' },
  { value: 'Other', label: 'Other' }
]

const Signup = () => {
  const navigate = useNavigate()
  const { handleCustomSignUp, loading } = useCustomAuth()
  const { schools, loading: loadingSchools } = useSchools()
  const { toast } = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [birthday, setBirthday] = useState('')
  const [selectedSchool, setSelectedSchool] = useState('')
  const [classValue, setClassValue] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)

  console.log('Signup page - Schools:', schools)
  console.log('Signup page - Loading schools:', loadingSchools)

  // Auto-select school based on email domain
  useEffect(() => {
    if (email && !selectedSchool && schools.length > 0) {
      const emailDomain = email.split('@')[1]
      if (emailDomain) {
        if (emailDomain === 'yopmail.com') {
          // Auto-select "Other" school for yopmail.com
          const otherSchool = schools.find((school: School) => school.name === 'Other')
          if (otherSchool) {
            setSelectedSchool(otherSchool.id)
          }
        } else {
          // Auto-select matching school for university emails
          const matchingSchool = schools.find((school: School) => school.domain === emailDomain)
          if (matchingSchool) {
            setSelectedSchool(matchingSchool.id)
          }
        }
      }
    }
  }, [email, schools, selectedSchool])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Check if terms are accepted
    if (!acceptedTerms) {
      toast({
        title: 'Terms and Conditions Required',
        description: 'Please accept the Terms and Conditions to continue.',
        variant: 'destructive',
      })
      return
    }
    
    // Email domain validation removed - users can use any email with any school

    const success = await handleCustomSignUp(
      email,
      password,
      firstName,
      lastName,
      birthday,
      selectedSchool,
      classValue
    )
    if (success) {
      navigate('/login')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-purple-100 p-6">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-purple-200 bg-white/80 backdrop-blur-xl shadow-[0_10px_40px_rgba(147,51,234,0.1)] p-6 sm:p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Create your account 🚀</h1>
            <p className="text-sm text-gray-600 mt-1">Join Dably and start your learning journey</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-gray-700">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                required
                disabled={loading}
                className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-purple-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-gray-700">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                required
                disabled={loading}
                className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-purple-400"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@university.edu"
              required
              disabled={loading}
              className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-purple-400"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Create a strong password"
              required
              disabled={loading}
              className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-purple-400"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthday" className="text-gray-700">Birthday</Label>
            <Input
              id="birthday"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              required
              disabled={loading}
              className="bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus-visible:ring-purple-400"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="school" className="text-gray-700">School</Label>
            <Select
              value={selectedSchool}
              onValueChange={setSelectedSchool}
              disabled={loading || loadingSchools}
            >
              <SelectTrigger className="bg-white border-gray-200 text-gray-900 focus-visible:ring-purple-400">
                <SelectValue placeholder={loadingSchools ? "Loading schools..." : "Select your school"} />
              </SelectTrigger>
              <SelectContent>
                {loadingSchools ? (
                  <SelectItem value="loading" disabled>Loading schools...</SelectItem>
                ) : schools.length === 0 ? (
                  <SelectItem value="empty" disabled>No schools available</SelectItem>
                ) : (
                  schools.map((school: School) => (
                    <SelectItem key={school.id} value={school.id}>
                      {school.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="class" className="text-gray-700">Class Level</Label>
            <Select
              value={classValue}
              onValueChange={setClassValue}
              disabled={loading}
            >
              <SelectTrigger className="bg-white border-gray-200 text-gray-900 focus-visible:ring-purple-400">
                <SelectValue placeholder="Select your class level" />
              </SelectTrigger>
              <SelectContent>
                {classOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Terms and Conditions */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms"
              checked={acceptedTerms}
              onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
              required
              className="mt-1"
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Terms and Conditions*
              </label>
              <p className="text-xs text-gray-500">
                I agree to the{' '}
                <a
                  href="https://docs.google.com/document/d/1LRDMOzieD6lVe6ykyBreTbSUyt96bf93/edit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-500 underline"
                >
                  Terms and Conditions
                </a>
                . This field is required to complete registration.
              </p>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-purple-500 hover:bg-purple-400 text-white shadow-[0_6px_30px_rgba(147,51,234,0.4)]"
            disabled={loading || loadingSchools}
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-gray-600 mt-5">
          Already have an Account?{" "}
          <Link
            to="/login"
            className="text-purple-600 hover:text-purple-500 font-medium"
          >
            Login
          </Link>
        </p>
      </div>
    </div>
  </div>
  )
}

export default Signup
